import type { HydratableFactory, Hydratable } from '@vimcar/mvvm/hydratable'
import type { Request } from '@vimcar/shared-utils/request'

import hydratable from '@vimcar/mvvm/hydratable'
import { stringifyQueryParams } from '@vimcar/shared-utils/utils'
import * as mobx from 'mobx'
import { Observable } from 'rxjs/Observable'
import * as o from 'rxjs/operators'

import type { CheckboxesNode } from './CheckboxesNode'
import type { ComplexNode } from './ComplexNode'
import type { CustomNode } from './CustomNode'
import type { KeyValuePair } from './KeyValuePair'
import type { LinkGroup } from './LinkGroup'
import type { NodeLink } from './NodeLink'
import type { SelectNode } from './SelectNode'
import type { ValuedNode, Values } from './ValuedNode'

import mkCheckboxesNode from './CheckboxesNode'
import mkComplexNode from './ComplexNode'
import mkCustomNode from './CustomNode'
import mkKeyValuePair from './KeyValuePair'
import mkLinkGroup from './LinkGroup'
import mkNodeLink from './NodeLink'
import mkSelectNode from './SelectNode'
import mkValuedNode from './ValuedNode'

export type CustomAttributeValue = void | null | string | Date

export type NodeTypes = SelectNode | ValuedNode | CustomNode

type _Nodes = {
  selectNodes: mobx.ObservableMap<SelectNode>
  customNodes: mobx.ObservableMap<CustomNode>
  valuedNodes: mobx.ObservableMap<ValuedNode>
  checkboxesNodes: mobx.ObservableMap<CheckboxesNode>
  complexNodes: mobx.ObservableMap<ComplexNode>
  nodeLinks: mobx.ObservableMap<NodeLink>
  linkGroups: mobx.ObservableMap<LinkGroup>
  keyValuePairs: mobx.ObservableMap<KeyValuePair>
  fetch: () => Observable<boolean>
  get: (
    string,
  ) =>
    | SelectNode
    | CustomNode
    | ValuedNode
    | CheckboxesNode
    | ComplexNode
    | NodeLink
    | LinkGroup
  getValueCollection: (args: Array<string>) => Values
}

export type ContentType =
  | 'selectNode'
  | 'customNode'
  | 'valuedNode'
  | 'complexNode'
  | 'selectNodeLink'
  | 'linkGroup'
  | 'keyValuePair'
  | 'checkboxesNode'

// @ts-expect-error@2314
export type Nodes = Hydratable<_Nodes>

export type Options = {
  request: Request
  contentfulBaseUrl: string
  contentfulNodesApiKey: string
  contentfulNodesSpaceId: string
}

// @ts-expect-error@2322
const mkNodes: HydratableFactory<_Nodes, object> = hydratable(
  ({
    request,
    contentfulBaseUrl,
    contentfulNodesApiKey,
    contentfulNodesSpaceId,
  }: Options) => {
    const nodesEntriesUri = `${contentfulBaseUrl}/spaces/${contentfulNodesSpaceId}/entries`
    // @ts-expect-error@2352
    const nodes = mobx.observable({
      selectNodes: mobx.observable.map(),
      customNodes: mobx.observable.map(),
      valuedNodes: mobx.observable.map(),
      checkboxesNodes: mobx.observable.map(),
      complexNodes: mobx.observable.map(),
      nodeLinks: mobx.observable.map(),
      keyValuePairs: mobx.observable.map(),
      linkGroups: mobx.observable.map(),

      fetch: mobx.action((locale) => {
        const cached = nodes
        const queryString = stringifyQueryParams()({
          access_token: contentfulNodesApiKey,
          limit: 1000,
          locale,
        })

        return Observable.merge(
          cached.selectNodes.size > 0
            ? Observable.of(true)
            : Observable.empty(),
          request(`${nodesEntriesUri}?${queryString}`).pipe(
            o.map(
              mobx.action((response) => {
                // @ts-expect-error@2339
                response.body.items.forEach((entry) => {
                  switch (entry.sys.contentType.sys.id) {
                    case 'selectNode':
                      nodes.selectNodes.set(
                        entry.sys.id,
                        mkSelectNode.fromBody({ nodes })(entry),
                      )
                      break
                    case 'customNode':
                      nodes.customNodes.set(
                        entry.sys.id,
                        mkCustomNode.fromBody({ nodes })(entry),
                      )
                      break
                    case 'valuedNode':
                      nodes.valuedNodes.set(
                        entry.sys.id,
                        mkValuedNode.fromBody({ nodes })(entry),
                      )
                      break
                    case 'complexNode':
                      nodes.complexNodes.set(
                        entry.sys.id,
                        mkComplexNode.fromBody({ nodes })(entry),
                      )
                      break
                    case 'selectNodeLink':
                      nodes.nodeLinks.set(
                        entry.sys.id,
                        mkNodeLink.fromBody({ nodes })(entry),
                      )
                      break
                    case 'linkGroup':
                      nodes.linkGroups.set(
                        entry.sys.id,
                        mkLinkGroup.fromBody({ nodes })(entry),
                      )
                      break
                    case 'keyValuePair':
                      nodes.keyValuePairs.set(
                        entry.sys.id,
                        // @ts-expect-error@2554
                        mkKeyValuePair.fromBody()(entry),
                      )
                      break
                    case 'checkboxesNode':
                      nodes.checkboxesNodes.set(
                        entry.sys.id,
                        mkCheckboxesNode.fromBody({ nodes })(entry),
                      )
                      break
                    default:
                      break
                  }
                })
                return Observable.of(true)
              }),
            ),
          ),
        )
      }),

      get(id) {
        return (
          nodes.selectNodes.get(id) ||
          nodes.valuedNodes.get(id) ||
          nodes.customNodes.get(id) ||
          nodes.checkboxesNodes.get(id) ||
          nodes.complexNodes.get(id) ||
          nodes.nodeLinks.get(id) ||
          nodes.linkGroups.get(id)
        )
      },

      getValueCollection(ids) {
        const values = {
          tags: [],
          attrs: mobx.observable.map(),
        }
        const valuedNodes = nodes.valuedNodes
          .values()
          .filter((vn) => ids.includes(vn.id))
          .sort((vn1, vn2) => ids.indexOf(vn1.id) - ids.indexOf(vn2.id))
        valuedNodes.forEach((vn) => {
          // @ts-expect-error@2339
          values.destination = vn.destination || values.destination
          // @ts-expect-error@2339
          values.group = vn.group || values.group
          // @ts-expect-error@2339
          values.subject = vn.subject || values.subject
          // @ts-expect-error@2339
          values.priority = vn.priority || values.priority
          // @ts-expect-error@2339
          values.team = vn.team || values.team
          // @ts-expect-error@2339
          values.pipedriveId = vn.pipedriveId || values.pipedriveId
          // @ts-expect-error@2339
          values.stageId = vn.stageId || values.stageId
          if (vn.tags) values.tags = vn.tags.concat(values.tags)
          if (vn.attributes) {
            vn.attributes.forEach((a) => values.attrs.set(a.key, a.value))
          }
        })
        values.tags.filter((tag, ix) => values.tags.indexOf(tag) === ix)
        return values
      },
    } as _Nodes)
    return nodes
  },
  'Nodes',
)

export default mkNodes
